import { LatLngTuple, LeafletMouseEvent } from 'leaflet';
import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useMap } from 'react-leaflet';
import { SearchButton } from './SearchButton';
import { CloseButton } from './CloseButton';
import {
  CUSTODIAN_CODES,
  MapMethodProps,
  addAsset,
  addressResponseType,
  checkTfLBoundaryAndDisplayPopup,
  geolocationOutOfBoroughPopup,
  getCustodianCodes,
  getNearestAddress,
  handleGetSuggestions,
  isLatLngWithinBoundary,
  outsideBoroughPopup,
} from '@utils';
import { NoResult } from './NoResult';
import { LocationError } from './LocationError';
import { CurrentLocation } from './CurrentLocation';
import { useGeolocationPermission } from '@hooks';
import { AssetConfig, AssetType } from '@types';

export const SearchControl = ({
  mapMethods,
  searchTerm,
  setSearchTerm,
  enableBuffer,
  enableTfLBoundaryCheck,
}: MapMethodProps) => {
  const map = useMap();
  const { clearGraphics, addMarker, onChange, assetType } = mapMethods;
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [noResults, setNoResults] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [focusedItemIndex, setFocusedItemIndex] = useState<number>(-1);
  const suggestionsRef = useRef<HTMLUListElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [mouseOver, setMouseOver] = useState(false);
  const [viewport, setViewport] = useState<number | null>(null);
  const navigationPermission = useGeolocationPermission();

  useEffect(() => {
    if (navigationPermission === 'granted') {
      searchInputRef.current?.focus();
      setLocationError(false);
      setSuggestions([]);
    }
  }, [navigationPermission]);

  const handleResize = () => {
    setViewport(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [window.innerWidth]);

  useEffect(() => {
    if (!map) return;

    const clearSuggestions = () => {
      if (suggestions.length > 0) {
        setSuggestions([]);
      }
    };

    map.on('movestart', clearSuggestions);
    map.on('touchmove', clearSuggestions);

    return () => {
      map.off('movestart', clearSuggestions);
      map.off('touchmove', clearSuggestions);
    };
  }, [map, suggestions]);

  const handleClear = () => {
    setSearchTerm('');
    setSuggestions([]);
    setNoResults(false);
    setLocationError(false);
    clearGraphics();
    setFocusedItemIndex(-1);
    onChange({} as addressResponseType);
  };

  const handleSearch = useCallback(async (term: string) => {
    if (term.length < 3) {
      setSuggestions([]);
      setFocusedItemIndex(-1);
      setNoResults(false);
      return;
    }

    try {
      const results = await handleGetSuggestions(
        {
          suggestTerm: term,
          sourceIndex: 0,
        },
        getCustodianCodes(assetType, enableBuffer),
      );
      setSuggestions(results);
      setNoResults(results.length === 0);
      setLocationError(false);
    } catch (error) {
      setNoResults(true);
      setLocationError(false);
    }
  }, []);

  const handleMouseOverSuggestions = (e: React.MouseEvent) => {
    setMouseOver(true);
    if (e.type === 'mouseover') {
      setFocusedItemIndex(-1);
    }
  };
  const handleMouseLeaveSuggestions = (e: React.MouseEvent) => {
    setMouseOver(false);
  };

  const debouncedSearch = useCallback(
    (term: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        handleSearch(term);
      }, 500);
    },
    [handleSearch],
  );

  useEffect(() => {
    if (suggestionsRef.current && focusedItemIndex >= 0) {
      const liElement = suggestionsRef.current.children[focusedItemIndex];
      if (liElement) {
        liElement.scrollIntoView({ block: 'nearest' });
      }
    }
    if (focusedItemIndex >= 0 && suggestionsRef.current && mouseOver) {
      const focusedElement = suggestionsRef.current.children[
        focusedItemIndex
      ] as HTMLElement;
      if (focusedElement) {
        focusedElement.focus();
      }
    }
  }, [focusedItemIndex, mouseOver]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    // If we have suggestions open, handle keyboard navigation within suggestions
    if (suggestions.length > 0) {
      switch (e.key) {
        case 'Enter':
          e.preventDefault();
          if (focusedItemIndex >= 0 && focusedItemIndex < suggestions.length) {
            handleSuggestionSelect(suggestions[focusedItemIndex]);
          } else {
            if (debounceTimerRef.current) {
              clearTimeout(debounceTimerRef.current);
            }
            handleSearch(searchTerm);
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          setFocusedItemIndex((prev) => {
            return prev < suggestions.length - 1 ? prev + 1 : 0;
          });
          setMouseOver(false); // Disable mouse hover effect during keyboard navigation
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedItemIndex((prev) => {
            return prev > 0 ? prev - 1 : suggestions.length - 1;
          });
          setMouseOver(false); // Disable mouse hover effect during keyboard navigation
          break;
        case 'Escape':
          setSuggestions([]);
          setNoResults(false);
          break;
        default:
          break;
      }
      // Prevent event propagation when we're handling address list navigation
      e.stopPropagation();
      return;
    }

    // If no suggestions are open, handle other cases
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        if (focusedItemIndex === suggestions.length) {
          handleUseCurrentLocation(e as React.KeyboardEvent<HTMLElement>);
        } else {
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }
          handleSearch(searchTerm);
        }
        break;
      case 'ArrowDown':
      case 'ArrowUp':
        // Only handle arrow keys for "use current location" when search is focused but empty
        if (isFocused && searchTerm === '') {
          e.preventDefault();
          if (e.key === 'ArrowDown') {
            setFocusedItemIndex(0); // Focus on "use current location"
          } else {
            setFocusedItemIndex(0); // Focus on "use current location"
          }
          setMouseOver(false);
        }
        // Otherwise, let the event propagate to handle custom map assets navigation
        break;
      case 'Tab':
        if (isFocused && searchTerm === '') {
          const maxIndex = 1; // Just "use current location"
          if (e.shiftKey) {
            setFocusedItemIndex((prev) => (prev > 0 ? prev - 1 : maxIndex - 1));
          } else {
            setFocusedItemIndex((prev) => (prev < maxIndex - 1 ? prev + 1 : 0));
          }
        }
        break;
      case 'Escape':
        setSuggestions([]);
        setNoResults(false);
        break;
    }
  };

  const handleSuggestionSelect = async (suggestion: addressResponseType) => {
    const newLocation: LatLngTuple = [
      parseFloat(suggestion.latitude),
      parseFloat(suggestion.longitude),
    ];
    map.setView(newLocation, 20);
    clearGraphics();

    if (
      isLatLngWithinBoundary(newLocation[0], newLocation[1], enableBuffer) ||
      !enableBuffer
    ) {
      addMarker(newLocation);
      setSuggestions([]);
      setNoResults(false);
      setLocationError(false);
      await addAsset(assetType, onChange, suggestion);
      setSearchTerm(suggestion.full_address);
      if (enableTfLBoundaryCheck) {
        const assetString =
          typeof (assetType as AssetType)[0] === 'string'
            ? assetType
            : (assetType as AssetConfig[])[0].asset;

        checkTfLBoundaryAndDisplayPopup(
          assetString as AssetType,
          suggestion,
          {
            latlng: { lat: newLocation[0], lng: newLocation[1] },
          } as LeafletMouseEvent,
          map,
        );
      }
    } else {
      outsideBoroughPopup(
        {
          latlng: { lat: newLocation[0], lng: newLocation[1] },
        } as LeafletMouseEvent,
        map,
      );
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTerm = e.target.value;
    setSearchTerm(newTerm);
    setNoResults(false);
    setLocationError(false);
    debouncedSearch(newTerm);
  };

  const stopPropagation = (
    e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>,
  ) => {
    e.stopPropagation();
  };

  const handleSearchButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    handleSearch(searchTerm);
  };

  const handleUseCurrentLocation = (
    e: React.MouseEvent | React.KeyboardEvent<HTMLElement>,
  ) => {
    e.stopPropagation();
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const nearestAddress = await getNearestAddress(latitude, longitude);
            if (nearestAddress) {
              const newLocation: LatLngTuple = [
                parseFloat(nearestAddress.latitude),
                parseFloat(nearestAddress.longitude),
              ];
              await addAsset(assetType, onChange, nearestAddress);
              map.setView(newLocation, 18);
              clearGraphics();
              addMarker(newLocation);
              setSearchTerm(nearestAddress.full_address);
            } else {
              console.error('No nearby address found');
              setLocationError(true);
            }
          } catch (error) {
            console.error('Error getting nearest address:', error);
            geolocationOutOfBoroughPopup(map);
          }
        },
        (error) => {
          console.error('Error getting geolocation:', error);
          setLocationError(true);
        },
      );
    } else {
      console.error('Geolocation is not supported by this browser');
    }
  };

  const isCursorInMapInput = () => {
    return searchInputRef.current === document.activeElement;
  };

  return (
    <div
      className="leaflet-control-search leaflet-bar"
      style={{
        position: 'absolute',
        top: '10px',
        left: '50px',
        right: '50px',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'white',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          minWidth: 'fit-content',
        }}
      >
        <input
          type="text"
          ref={searchInputRef}
          value={searchTerm}
          id="map-search-input"
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 200)}
          placeholder="Search for an address or business"
          style={{
            flex: 1,
            border: 'none',
            padding: '10px 15px',
            fontSize: '16px',
            outline: 'none',
            height: '50px',
            maxWidth: `${viewport && viewport < 400 ? '45vw' : ''}`,
          }}
          onMouseMove={stopPropagation}
          onDoubleClickCapture={stopPropagation}
          onClick={stopPropagation}
          onDoubleClick={stopPropagation}
          autoComplete="off"
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {(searchTerm ||
            suggestions.length > 0 ||
            noResults ||
            locationError) && (
            <>
              <CloseButton handleClear={handleClear} />
              <div
                style={{
                  width: '1px',
                  height: '20px',
                  backgroundColor: '#ccc',
                  margin: '0 10px',
                }}
              />
            </>
          )}
          <SearchButton handleSearchButtonClick={handleSearchButtonClick} />
        </div>
      </div>
      {noResults && (
        <NoResult
          handleMouseOverSuggestions={handleMouseOverSuggestions}
          handleMouseLeaveSuggestions={handleMouseLeaveSuggestions}
          stopPropagation={stopPropagation}
          searchTerm={searchTerm}
        />
      )}
      {isCursorInMapInput() &&
        !locationError &&
        !noResults &&
        suggestions.length === 0 &&
        searchTerm === '' && (
          <CurrentLocation
            handleUseCurrentLocation={handleUseCurrentLocation}
            handleMouseOverSuggestions={handleMouseOverSuggestions}
            handleMouseLeaveSuggestions={handleMouseLeaveSuggestions}
            stopPropagation={stopPropagation}
            isFocused={focusedItemIndex === suggestions.length}
          />
        )}
      {locationError && (
        <LocationError
          handleMouseOverSuggestions={handleMouseOverSuggestions}
          handleMouseLeaveSuggestions={handleMouseLeaveSuggestions}
          stopPropagation={stopPropagation}
        />
      )}
      {suggestions.length > 0 && (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: '5px 0 0 0',
            backgroundColor: 'white',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            maxHeight: '300px',
            overflowY: 'auto',
          }}
          ref={suggestionsRef}
          onMouseOver={handleMouseOverSuggestions}
          onMouseLeave={handleMouseLeaveSuggestions}
          onDrag={stopPropagation}
          onDragCapture={stopPropagation}
          onDoubleClick={stopPropagation}
          onDoubleClickCapture={stopPropagation}
          onMouseMove={stopPropagation}
          onClick={stopPropagation}
          onTouchMove={stopPropagation}
          onWheel={stopPropagation}
          onWheelCapture={stopPropagation}
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={index}
              onClick={() => handleSuggestionSelect(suggestion)}
              onKeyDown={handleKeyDown}
              tabIndex={0}
              role="option"
              aria-selected={index === focusedItemIndex}
              style={{
                padding: '10px 15px',
                borderBottom:
                  index < suggestions.length - 1 ? '1px solid #eee' : 'none',
                cursor: 'pointer',
                fontSize: '14px',
              }}
              className={`hover:bg-gray-200 ${index === focusedItemIndex && !mouseOver ? 'bg-gray-200' : ''}`}
            >
              {suggestion.full_address}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
